import { Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { BookingStep } from '../../components/booking/BookingStep'
import { BookingListCard } from '../../components/bookings/BookingListCard'
import { EventBookingListCard } from '../../components/bookings/EventBookingListCard'
import { api } from '../../core/trpc'

const PATH = '/bookings/'

const BookingsIndexPage = () => {
    const { t } = useTranslation()

    const { data: videoBookings, isPending: isVideoPending } =
        api.appointment.getUserAppointments.useQuery({})
    /**
     * A third call, because the list procedure filters on one service type at a time and defaults
     * to video consultations. A webinar booking is an Appointment like any other — it was created,
     * it has a join token, and it simply matched neither of the other two queries.
     */
    const { data: webinarBookings, isPending: isWebinarPending } =
        api.appointment.getUserAppointments.useQuery({ serviceType: 'WEBINAR' })
    const { data: careBookings, isPending: isCarePending } =
        api.appointment.getUserExaminationAppointments.useQuery()

    const { data: eventBookings, isPending: isEventPending } =
        api.eventTicket.getMyBookings.useQuery()

    const isPending = isVideoPending || isWebinarPending || isCarePending || isEventPending
    const bookings = [
        ...[...(videoBookings ?? []), ...(webinarBookings ?? []), ...(careBookings ?? [])].map(
            (booking) => ({ kind: 'appointment' as const, booking }),
        ),
        ...(eventBookings ?? []).map((booking) => ({ kind: 'event' as const, booking })),
    ].sort(
        (a, b) => new Date(b.booking.startDate).getTime() - new Date(a.booking.startDate).getTime(),
    )

    return (
        <BookingStep title={t('frontend.auth.myBookings')} step={t('frontend.booking.title')}>
            {isPending ? (
                <Skeleton className="h-64 w-full" />
            ) : bookings.length === 0 ? (
                <div className="flex flex-col items-start gap-4">
                    <p className="text-brand-gray">{t('frontend.bookings.empty')}</p>
                    {/*
                     * The booking flows the home page leads to, not the catalogue listings: someone
                     * with nothing booked wants to book, and a listing is a detour on the way.
                     */}
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Link
                            to="/booking/video-consultation"
                            className="rounded-full bg-primary px-6 py-3 font-bold text-white transition-colors hover:bg-brand-dark"
                        >
                            {t('frontend.home.bookVideoConsultation')}
                        </Link>
                        <Link
                            to="/booking/examination"
                            className="rounded-full border-2 border-primary px-6 py-3 font-bold text-primary transition-colors hover:bg-primary hover:text-white"
                        >
                            {t('frontend.home.bookExamination')}
                        </Link>
                    </div>
                </div>
            ) : (
                <ul className="flex flex-col gap-3">
                    {bookings.map((item) => (
                        <li key={`${item.kind}-${item.booking.id}`}>
                            {item.kind === 'event' ? (
                                <EventBookingListCard booking={item.booking} />
                            ) : (
                                <BookingListCard booking={item.booking} />
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    head: () => ({ meta: [{ title: 'Long-Y - Foglalásaim' }] }),
    component: BookingsIndexPage,
})
